import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  forwardRef,
  memo,
} from "react";
import { useJackpotPlates } from "../../../Games/Shared/jackpotPlates";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { selectFreeSpinsActive } from "../../../State/selectors";
import { selectHoldAndWinActive } from "../../../State/Slices/holdAndWinSlice";
import { useDimensionsWhen } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import { Settings } from "../../../settings";
import {
  useSlotRegistry,
  useSlot,
} from "../../../Contexts/SlotRegistryContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import FreeSpinsGameViewController, {
  FREE_SPINS_GAME_VIEW_SLOTS,
} from "./FreeSpinsGameViewController";
import {
  NATIVE_REELSET_SIZE,
  resolveBoardPlacement,
} from "../GameViewSimple/GameViewSimpleController";
import FreeSpinBasicReelSet from "../../../Games/FreeSpinBasic/FreeSpinBasicReelSet/FreeSpinBasicReelSet";
import FreeSpinBasicOverlayReelSet from "../../../Games/FreeSpinBasic/FreeSpinBasicOverlayReelSet/FreeSpinBasicOverlayReelSet";
import DimReelSet from "../../../Games/Shared/DimReelSet/DimReelSet";
import WinBoxContainer from "../../../Games/Shared/WinBoxReelSet/WinBoxContainer";
import WinLinesContainer from "../../../Games/Shared/WinLinesReelSet/WinLinesContainer";
import { WinTextSized } from "../WinText/WinText";
import { WIN_TEXT_CONFIG } from "../WinText/WinTextController";
import { FEATURES_CONFIG } from "../../../featuresConfig";

// Fine-tuning offsets for the injected containers, in spine-local units.
// Both containers stack on the same grid, so they must move together.
const REELSET_X_OFFSET = 0;
const REELSET_Y_OFFSET = 0;

// ============================================================================
// FreeSpinsGameView — the free spins room. Same structure as GameViewSimple:
//
//   FreeSpinsGameView (shell)   reads the size while the room is live and
//                               applies it imperatively in layout effects: the
//                               spine's placement, the no-spine backup, the
//                               coin trail layer's size.
//   FreeSpinsScene (memo)       the spine, the board and its overlay. Its
//                               props never change with the size, so a resize
//                               does not render it.
//
// The board (reels, dim, overlay, win boxes, win text) is built in the spine's
// NATIVE units (NATIVE_REELSET_SIZE) and inherits the spine's scale, so a
// resize is the spine's scale and position — nothing inside changes.
//
// The room READS state and never writes it: it is on screen while
// `freeSpins.active` (and no Hold & Win covers it). GameViewSimple reads the
// same flag with the opposite sign. Hidden by alpha 0.001, never `visible`,
// so the spine stays warm — see GameViewSimple.
// ============================================================================

/** The overlay's children, in native units. */
const FreeSpinsOverlayContent = () => {
  const { symbolWidth, symbolHeight, reelSpacing } = NATIVE_REELSET_SIZE;
  const {
    freeSpinBasicWinBoxContainerRef,
    freeSpinBasicGlobalLineWinTextRef,
    freeSpinBasicLineWinTextRef,
  } = useReelRefs();
  return (
    <>
      <FreeSpinBasicOverlayReelSet
        symbolWidth={symbolWidth}
        symbolHeight={symbolHeight}
        reelSpacing={reelSpacing}
      />

      {/* Win lines UNDER the win boxes and the symbols — 2026-10-13. */}
      {/* Moved to the reel container (FreeSpinsScene): its window clip hides
          the oversized ends under the pillars. */}
      {/* {FEATURES_CONFIG.WIN_LINES.ENABLED && (
        <WinLinesContainer
          ref={freeSpinBasicWinLinesContainerRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          zIndex={2}
        />
      )} */}

      {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
        <Container name="fs_win_text_overlay" zIndex={6}>
          <WinTextSized
            ref={freeSpinBasicGlobalLineWinTextRef}
            fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
            skin={WIN_TEXT_CONFIG.GLOBAL_WIN_SKIN}
            hideOnBigWin={true}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
          <WinTextSized
            ref={freeSpinBasicLineWinTextRef}
            fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
            skin={WIN_TEXT_CONFIG.LINE_WIN_SKIN}
            hideOnBigWin={true}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        </Container>
      )}

      {FEATURES_CONFIG.WIN_BOX.ENABLED && (
        <WinBoxContainer
          ref={freeSpinBasicWinBoxContainerRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />
      )}
    </>
  );
};

/**
 * The scene: the free_spins_game_view spine with the board injected into its
 * two reel slots. Memoized; the refs are the shell's so it can place them.
 * The controller is published on `freeSpinsGameViewControllerRef` for the
 * free spins win controller.
 */
const FreeSpinsScene = memo(
  ({
    sizeRef,
    controllerRef,
    containerRef,
    reelContainerRef,
    overlayContainerRef,
    coinTrailContainerRef,
    reelSlot,
    overlaySlot,
  }) => {
    // The jackpot plates' values, on the current bet (Games/Shared/jackpotPlates).
    useJackpotPlates(controllerRef);

    const slotRegistry = useSlotRegistry();
    // const { freeSpinsGameViewControllerRef, freeSpinBasicDimReelSetRef } =
    //   useReelRefs();
    const {
      freeSpinsGameViewControllerRef,
      freeSpinBasicDimReelSetRef,
      freeSpinBasicWinLinesContainerRef,
    } = useReelRefs();

    const freeSpinsActive = useSelector(selectFreeSpinsActive);
    // A Hold & Win won inside free spins covers this room; FS stays active
    // underneath.
    const holdAndWinActive = useSelector(selectHoldAndWinActive);

    // ─── Controller ───
    useEffect(() => {
      if (!containerRef.current || controllerRef.current) return;

      // The size as it is now — the shell writes it on every render.
      const {
        dimensions,
        symbolWidth,
        sceneCellHeight,
        sceneScale,
        device,
        orientation,
        layout,
        isMobileSmall,
      } = sizeRef.current;
      const realDevice = isMobileSmall
        ? PixiSettings.DEVICE.MOBILE_SMALL
        : device;

      controllerRef.current = new FreeSpinsGameViewController(
        containerRef.current,
        dimensions,
        symbolWidth,
        sceneCellHeight,
        sceneScale,
        realDevice,
        orientation,
        layout,
        slotRegistry,
      );
      // The shell's resize effect ran before the controller existed, so the
      // first resize happens here.
      controllerRef.current.resize(
        dimensions,
        symbolWidth,
        sceneCellHeight,
        sceneScale,
        realDevice,
        orientation,
        layout,
      );

      if (freeSpinsGameViewControllerRef) {
        freeSpinsGameViewControllerRef.current = controllerRef.current;
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.destroy();
          controllerRef.current = null;
          if (freeSpinsGameViewControllerRef) {
            freeSpinsGameViewControllerRef.current = null;
          }
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Hidden, the room's spines sleep ─── same flags as the alpha below:
    // asleep from boot and under a Hold & Win won here. Flipped at the wipe's
    // midpoint. See spineDormancy.
    const roomHidden = !(freeSpinsActive && !holdAndWinActive);
    useEffect(() => {
      controllerRef.current?.setDormant(roomHidden);
    }, [roomHidden, controllerRef]);

    // ─── Inject the reel container into the spine's reel slot ───
    // In the slot the bone places and scales it, so whatever the shell's
    // backup gave it is reset first. If the slot goes away while the room
    // stays, the container goes back to the room for the backup to place.
    useEffect(() => {
      if (!reelSlot || !reelContainerRef.current) return;
      const node = reelContainerRef.current;
      node.position.set(REELSET_X_OFFSET, REELSET_Y_OFFSET);
      node.scale.set(1);
      reelSlot.addChild(node);
      const host = containerRef.current;
      return () => {
        if (node.parent !== reelSlot) return;
        reelSlot.removeChild(node);
        if (!node.destroyed && host && !host.destroyed) host.addChild(node);
      };
    }, [reelSlot, containerRef, reelContainerRef]);

    // ─── Inject the overlay container into the spine's overlay slot ───
    useEffect(() => {
      if (!overlaySlot || !overlayContainerRef.current) return;
      const node = overlayContainerRef.current;
      node.position.set(REELSET_X_OFFSET, REELSET_Y_OFFSET);
      node.scale.set(1);
      overlaySlot.addChild(node);
      const host = containerRef.current;
      return () => {
        if (node.parent !== overlaySlot) return;
        overlaySlot.removeChild(node);
        if (!node.destroyed && host && !host.destroyed) host.addChild(node);
      };
    }, [overlaySlot, containerRef, overlayContainerRef]);

    const { symbolWidth, symbolHeight, dividerDimensions, reelSpacing } =
      NATIVE_REELSET_SIZE;

    return (
      <Container
        ref={containerRef}
        name="free_spins_game_view"
        sortableChildren
        // The same layer as game_view — the two rooms never share the screen.
        zIndex={0}
        // The H&W preview (dev) hides it too, as a real Hold & Win does.
        alpha={freeSpinsActive && !holdAndWinActive && !FEATURES_CONFIG.DEV_TOOLS.PREVIEW_HOLD_AND_WIN_VIEW ? 1 : 0.001}
      >
        {/* Reels — injected into the spine's reel slot, clipped to the window */}
        <Container
          ref={reelContainerRef}
          name="free_spins_reel_container"
          x={REELSET_X_OFFSET}
          y={REELSET_Y_OFFSET}
          sortableChildren
          eventMode="passive"
        >
          {/* The board exists only while the room is live. `active` flips at
              the wipe's midpoint, in both directions, so it is built and
              destroyed under full cover. The containers themselves stay:
              they are injected into the spine's slots. */}
          {freeSpinsActive && (
            <>
              <FreeSpinBasicReelSet
                symbolWidth={symbolWidth}
                symbolHeight={symbolHeight}
                dividerDimensions={dividerDimensions}
                reelSpacing={reelSpacing}
              />

              {/* Oversized on purpose: the container is clipped to the frame
                  window by `reels_window_clip`, so the dim covers the window
                  edge to edge. */}
              <DimReelSet
                ref={freeSpinBasicDimReelSetRef}
                symbolWidth={symbolWidth}
                symbolHeight={symbolHeight}
                dividerDimensions={dividerDimensions}
                paddingMultiplier={0.1}
              />
            </>
          )}

          {/* Win lines (2026-10-13): above the reels and the dim, under the
              overlay's symbols and boxes; the window clip hides the oversized
              ends under the pillars. Outside `freeSpinsActive` — mounted and
              warm for good (spines stay mounted). */}
          {FEATURES_CONFIG.WIN_LINES.ENABLED && (
            <WinLinesContainer
              ref={freeSpinBasicWinLinesContainerRef}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              zIndex={3}
            />
          )}
        </Container>

        {/* Overlay — injected into the spine's overlay slot, above the frame */}
        <Container
          ref={overlayContainerRef}
          name="free_spins_overlay_reel_container"
          x={REELSET_X_OFFSET}
          y={REELSET_Y_OFFSET}
          zIndex={2}
          sortableChildren
          eventMode="passive"
        >
          {freeSpinsActive && <FreeSpinsOverlayContent />}
        </Container>

        {/* The coins' fire trails into the logo, above the whole room. Sized
            in pixels by the shell (coinToLogoTrail). */}
        <Container
          ref={coinTrailContainerRef}
          name="free_spins_coin_trail_container"
          zIndex={3}
          sortableChildren
          eventMode="passive"
        />
      </Container>
    );
  },
);

FreeSpinsScene.displayName = "FreeSpinsScene";

/**
 * FreeSpinsGameView: the shell. Reads the size and applies it imperatively;
 * renders the memoized scene, which a resize leaves alone.
 */
const FreeSpinsGameView = forwardRef((props, ref) => {
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);
  const overlayContainerRef = useRef(null);
  const coinTrailContainerRef = useRef(null);
  const { freeSpinsCoinTrailRef } = useReelRefs();

  // Follows the size only while the room is live; hidden, a resize costs it
  // nothing, and it catches up in the render that brings it on screen (at the
  // wipe's midpoint, under cover).
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  const size = useDimensionsWhen(freeSpinsActive);
  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    symbolScaleExact,
    device,
    orientation,
    layout,
    isMobileSmall,
  } = size;
  // The spine and its native board are scaled as a whole, so they take the
  // unrounded scale: continuous while the window is dragged.
  const sceneScale = symbolScaleExact ?? symbolScale;
  const sceneCellHeight = Settings.SYMBOL_HEIGHT * sceneScale;
  // The scene reads the size once, at mount (the controller).
  const sizeRef = useRef(null);
  sizeRef.current = { ...size, sceneScale, sceneCellHeight };

  const reelSlot = useSlot(FREE_SPINS_GAME_VIEW_SLOTS.REEL_SET);
  const overlaySlot = useSlot(FREE_SPINS_GAME_VIEW_SLOTS.OVERLAY_REEL_SET);

  // ─── The spine: scale, position, skin, reel block ───
  // A layout effect: placed in the same commit as the new size, before PIXI
  // draws the next frame.
  // Also every time the room comes on screen (a live trigger), so its
  // settings are always the ones in force — not only after a resize.
  useLayoutEffect(() => {
    if (!controllerRef.current || !freeSpinsActive) return;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;
    controllerRef.current.resize(
      dimensions,
      symbolWidth,
      sceneCellHeight,
      sceneScale,
      realDevice,
      orientation,
      layout,
    );
  }, [
    freeSpinsActive,
    dimensions,
    symbolWidth,
    sceneCellHeight,
    sceneScale,
    device,
    orientation,
    layout,
    isMobileSmall,
  ]);

  // ─── BACKUP: no spine slot to inject into ───
  // The containers are then plain children of the room (the spine's parent)
  // and get the transform the spine would have given them: its position and
  // scale, times the reel-block scale. No frame or window clip without the
  // spine; the reel set's own mask still clips the reels.
  useLayoutEffect(() => {
    if (reelSlot && overlaySlot) return;
    const board = resolveBoardPlacement({
      dimensions,
      symbolHeight: sceneCellHeight,
      symbolScale: sceneScale,
      layout,
    });
    const reels = reelContainerRef.current;
    if (reels && !reelSlot) {
      reels.position.set(board.x, board.y);
      reels.scale.set(board.scale * board.reelScale);
    }
    const overlay = overlayContainerRef.current;
    if (overlay && !overlaySlot) {
      overlay.position.set(board.x, board.y);
      overlay.scale.set(board.scale * board.reelScale);
    }
  }, [reelSlot, overlaySlot, dimensions, sceneCellHeight, sceneScale, layout]);

  // ─── The coins' fire trails into the logo (GDD §5.1) ───
  // Published with the live pixel symbol height, which sizes the fire
  // (Games/Shared/coinToLogoTrail).
  useEffect(() => {
    if (!freeSpinsCoinTrailRef) return undefined;
    freeSpinsCoinTrailRef.current = {
      trailContainer: coinTrailContainerRef.current,
      symbolHeight,
    };
    return () => {
      freeSpinsCoinTrailRef.current = null;
    };
  }, [freeSpinsCoinTrailRef, symbolHeight]);

  return (
    <FreeSpinsScene
      sizeRef={sizeRef}
      controllerRef={controllerRef}
      containerRef={containerRef}
      reelContainerRef={reelContainerRef}
      overlayContainerRef={overlayContainerRef}
      coinTrailContainerRef={coinTrailContainerRef}
      reelSlot={reelSlot}
      overlaySlot={overlaySlot}
    />
  );
});

FreeSpinsGameView.displayName = "FreeSpinsGameView";

export default FreeSpinsGameView;
